import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import axios from "axios";

const AssetUpdateModal = ({ open, onClose, cAssetDataArr }) => {
  const [assetStatus, setAssetStatus] = useState("");
  const [warrantyStatus, setWarrantyStatus] = useState("");
  const [warrantyDate, setWarrantyDate] = useState("");
  const [os, setOs] = useState(""); // Added OS state

  // Store original values to compare changes
  const [originalValues, setOriginalValues] = useState({
    assetStatus: "",
    warrantyStatus: "",
    warrantyDate: "",
    os: "" // Added OS to original values
  });

  const { handleToastOpen } = useToast();

  const warrantyDropdownArr = ["Active", "Expired"];
  const osArr = cAssetDataArr?.os_system || []
  const assetStatusDropdownArr = [
    "Assigned",
    "Available",
    "Maintenance",
    "Sold",
    "Obsolete",
  ];

  // Define which fields are editable
  const editableFields = ["asset_status", "warranty_status", "warranty_date", "os"];
  
  // Define fields that should be hidden from non-editable display
  const hiddenFields = ["os_system"];

  // Function to format field names for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .replace(/([A-Z])/g, " $1")
      .trim();
  };

  // Function to get non-editable fields for display
  const getNonEditableFields = () => {
    if (!cAssetDataArr) return [];

    const fields = [];
    Object.entries(cAssetDataArr).forEach(([key, value]) => {
      // Skip editable fields, hidden fields, and null/undefined/empty values
      if (
        !editableFields.includes(key) &&
        !hiddenFields.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== "null"
      ) {
        fields.push({ key, value, label: formatFieldName(key) });
      }
    });

    return fields;
  };

  // Check if OS field should be visible (has data or is not null/empty)
  const shouldShowOsField = () => {
    return cAssetDataArr && 
           cAssetDataArr.os !== null && 
           cAssetDataArr.os !== undefined && 
           cAssetDataArr.os !== "" && 
           cAssetDataArr.os !== "null";
  };

  // Initialize form values when modal opens or data changes
  useEffect(() => {
    if (open && cAssetDataArr) {
      // Set asset status - find matching value (case insensitive)
      const matchingAssetStatus = assetStatusDropdownArr.find(
        (status) =>
          status.toLowerCase() === cAssetDataArr.asset_status?.toLowerCase()
      );
      const assetStatusValue = matchingAssetStatus || "";

      // Set warranty status - find matching value (case insensitive)
      const matchingWarrantyStatus = warrantyDropdownArr.find(
        (status) =>
          status.toLowerCase() === cAssetDataArr.warranty_status?.toLowerCase()
      );
      const warrantyStatusValue = matchingWarrantyStatus || "";

      // FIXED: OS initialization - only set the current OS, not all dropdown values
      const currentOs = cAssetDataArr.os || ""; // this should be a string
      const matchingOs = osArr.find(
        (osOption) =>
          osOption.toLowerCase() === currentOs?.toLowerCase()
      );
      const osValue = matchingOs || currentOs || ""; // Use the matching OS or current OS
      setOs(osValue);

      // Set warranty date - handle various null/undefined/empty cases
      let warrantyDateValue = "";
      if (
        cAssetDataArr.warranty_date &&
        cAssetDataArr.warranty_date !== null &&
        cAssetDataArr.warranty_date !== "" &&
        cAssetDataArr.warranty_date !== "null"
      ) {
        // Convert date format if needed (assuming DD/MM/YYYY to YYYY-MM-DD for input)
        const dateStr = cAssetDataArr.warranty_date;
        if (dateStr.includes("/")) {
          const [day, month, year] = dateStr.split("/");
          warrantyDateValue = `${year}-${month.padStart(2, "0")}-${day.padStart(
            2,
            "0"
          )}`;
        } else {
          warrantyDateValue = dateStr;
        }
      }

      // Set current values
      setAssetStatus(assetStatusValue);
      setWarrantyStatus(warrantyStatusValue);
      setWarrantyDate(warrantyDateValue);

      // Store original values for comparison
      setOriginalValues({
        assetStatus: assetStatusValue,
        warrantyStatus: warrantyStatusValue,
        warrantyDate: warrantyDateValue,
        os: osValue // Use the properly set osValue
      });
    }
  }, [open, cAssetDataArr]);

  // Validation function
  const validateForm = () => {
    // Only validate warranty date if user has entered a value and then cleared it
    // Or if there was an original date and user cleared it
    if (originalValues.warrantyDate && !warrantyDate) {
      handleToastOpen("error", "Please select a warranty date");
      return false;
    }

    // If user entered a date but then cleared it, show validation
    if (
      warrantyDate === "" &&
      originalValues.warrantyDate === "" &&
      (assetStatus !== originalValues.assetStatus ||
        warrantyStatus !== originalValues.warrantyStatus ||
        os !== originalValues.os)
    ) {
      // This is okay - user is just updating other fields without date
      return true;
    }

    return true;
  };

  // Function to get only changed fields
  const getChangedFields = () => {
    const changedFields = {
      asset_id: cAssetDataArr.asset_id, // Always include asset_id
    };

    // Only include fields that have changed
    if (assetStatus !== originalValues.assetStatus) {
      changedFields.asset_status = assetStatus;
    }

    if (warrantyStatus !== originalValues.warrantyStatus) {
      changedFields.warranty_status = warrantyStatus;
    }

    if (os !== originalValues.os) {
      changedFields.os = os;
    }

    if (warrantyDate !== originalValues.warrantyDate) {
      if (warrantyDate) {
        // Convert date back to DD/MM/YYYY format for API
        const [year, month, day] = warrantyDate.split("-");
        changedFields.warranty_date = `${day}/${month}/${year}`;
      } else {
        // If date is cleared, send empty string or null
        changedFields.warranty_date = "";
      }
    }

    return changedFields;
  };

  const onSave = async () => {
    if (!validateForm()) {
      return;
    }

    const changedFields = getChangedFields();

    // Check if anything has actually changed
    if (Object.keys(changedFields).length === 1) {
      // Only asset_id present
      handleToastOpen("error", "No changes detected");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.put(
        `${process.env.REACT_APP_BE_URL}/asset_it/update-asset`,
        changedFields,
        {
          headers: {
            "x-api-key": token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        handleToastOpen("success", "Asset updated successfully");
        // Only reset form and close on success
        resetForm();
        onClose();
        window.location.reload(); // Reload the page
      }
    } catch (error) {
      // Don't reset form on error - keep user's changes
      handleToastOpen("error", error.response?.data?.message || error.message);
    }
  };

  const resetForm = () => {
    // Reset to original default values from cAssetDataArr, not empty values
    if (cAssetDataArr) {
      const matchingAssetStatus = assetStatusDropdownArr.find(
        (status) =>
          status.toLowerCase() === cAssetDataArr.asset_status?.toLowerCase()
      );
      const assetStatusValue =
        matchingAssetStatus || cAssetDataArr.asset_status || "";

      const matchingWarrantyStatus = warrantyDropdownArr.find(
        (status) =>
          status.toLowerCase() === cAssetDataArr.warranty_status?.toLowerCase()
      );
      const warrantyStatusValue =
        matchingWarrantyStatus || cAssetDataArr.warranty_status || "";

      // FIXED: Reset OS to the correct current value
      const currentOs = cAssetDataArr.os || "";
      const matchingOs = osArr.find(
        (osOption) =>
          osOption.toLowerCase() === currentOs?.toLowerCase()
      );
      const osValue = matchingOs || currentOs || "";

      let warrantyDateValue = "";
      if (
        cAssetDataArr.warranty_date &&
        cAssetDataArr.warranty_date !== null &&
        cAssetDataArr.warranty_date !== "" &&
        cAssetDataArr.warranty_date !== "null"
      ) {
        const dateStr = cAssetDataArr.warranty_date;
        if (dateStr.includes("/")) {
          const [day, month, year] = dateStr.split("/");
          warrantyDateValue = `${year}-${month.padStart(2, "0")}-${day.padStart(
            2,
            "0"
          )}`;
        } else {
          warrantyDateValue = dateStr;
        }
      }

      setAssetStatus(assetStatusValue);
      setWarrantyStatus(warrantyStatusValue);
      setWarrantyDate(warrantyDateValue);
      setOs(osValue);
      setOriginalValues({
        assetStatus: assetStatusValue,
        warrantyStatus: warrantyStatusValue,
        warrantyDate: warrantyDateValue,
        os: osValue
      });
    } else {
      // Fallback to empty values if no cAssetDataArr
      setAssetStatus("");
      setWarrantyStatus("");
      setWarrantyDate("");
      setOs("");
      setOriginalValues({
        assetStatus: "",
        warrantyStatus: "",
        warrantyDate: "",
        os: ""
      });
    }
  };

  const handleClose = () => {
    // Reset form when closing
    resetForm();
    onClose();
  };

  const modalStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const paperStyle = {
    backgroundColor: "#ffffff",
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
    padding: "20px",
    textAlign: "center",
    borderRadius: "5px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    position: "relative",
    maxHeight: "90vh",
    overflowY: "auto",
  };

  // Get non-editable fields for display
  const nonEditableFields = getNonEditableFields();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="asset-update-modal-title"
      aria-describedby="asset-update-modal-description"
      style={modalStyle}
    >
      <Box sx={{ ...paperStyle, width: "600px"}}>
        {/* Close Icon */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#555",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            textAlign: "start",
            marginBottom: "1.5em",
            paddingRight: "40px",
          }}
        >
          Update Asset Details
        </Typography>

        {/* All fields display in merged section */}
        <Grid container spacing={2}>
          {/* Non-editable fields */}
          {nonEditableFields.map((field, index) => (
            <Grid item xs={6} key={field.key}>
              <TextField
                fullWidth
                label={field.label}
                value={field.value}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "#f5f5f5",
                    cursor: "default",
                    color: "#b8b8b8",
                    fontSize: "14px",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.23)", // Keep original border color on hover
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.23)", // Keep original border color when focused
                      borderWidth: "1px", // Keep original border width
                    },
                  },
                  mb: 0.5,
                }}
              />
            </Grid>
          ))}

          {/* Asset Status Dropdown */}
          <Grid item xs={6}>
            <FormControl fullWidth >
              <InputLabel id="asset-status-label">Asset Status</InputLabel>
              <Select
                labelId="asset-status-label"
                id="asset-status-select"
                value={assetStatus}
                label="Asset Status"
                onChange={(e) => setAssetStatus(e.target.value)}
                size="small"
                sx={{
                  textAlign: "left",
                  fontSize: "15px",
                  mt: 0.5,
                  "& .MuiSelect-select": {
                    display: "flex",
                    justifyContent: "flex-start",
                  },
                }}
              >
                {assetStatusDropdownArr.map((status) => (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{
                      fontSize: "15px",
                    }}
                  >
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Warranty Status Dropdown */}
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Warranty Status"
              value={warrantyStatus}
              onChange={(e) => setWarrantyStatus(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                mt:0.5,
                textAlign:'left',
                fontSize:'15px'
              }}
            >
              {warrantyDropdownArr.map((status) => (
                <MenuItem key={status} value={status} sx={{
                  textAlign:'left'
                }}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Operating System Dropdown - Only show if OS data exists */}
          {shouldShowOsField() && (
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Operating System"
                value={os}
                onChange={(e) => setOs(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mt: 0.5,
                  textAlign: 'left',
                  fontSize: '15px'
                }}
              >
                {osArr.map((osOption) => (
                  <MenuItem key={osOption} value={osOption} sx={{
                    textAlign: 'left'
                  }}>
                    {osOption}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Warranty Date Picker */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              id="warranty-date"
              label="Warranty Date"
              type="date"
              sx={{
                fontSize: "15px",
                mt:0.5
              }}
              value={warrantyDate}
              onChange={(e) => setWarrantyDate(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            sx={{ ml: 2 }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssetUpdateModal;